import { useEffect, useRef, useState } from 'react';
import { uri, useAuth, getFreshToken, post } from '../../api';
import { createGame } from 'phaser-wars';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import Conversation from '../../components/Conversation';
import Dropdown from '../../components/dropwdown';

export default function GamePage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const container = useRef<HTMLDivElement>(null);
  const auth = useAuth();

  const [openChat, setOpenChat] = useState(false);

  const isSpectator =
    new URLSearchParams(location.search).get('spectate') === 'true';

  const fromCampaign =
    new URLSearchParams(location.search).get('from') === 'campaign';

  const backUrl = '/my-games';

  const handleOpenChat = () => setOpenChat(true);
  const handleCloseChat = () => setOpenChat(false);

  const handleExit = () => navigate(backUrl);

  // Stashes the current game URL in location state (same { from: location }
  // shape App.tsx's RequireAuth/PublicOnly already use for post-login
  // redirects) so the Settings page can offer an easy way back instead of
  // relying on the browser's own back button.
  const handleOpenSettings = () =>
    navigate('/settings', { state: { from: location } });

  // A full reload (not react-router's navigate) — GamePage's game-creation
  // effect only runs once on mount (see below), so a same-route,
  // query-only navigation to ?spectate=true wouldn't actually recreate the
  // Phaser game in spectator mode. Reloading guarantees a fresh mount with
  // the right value. Not passed to createGame in campaign missions (see
  // below), where spectating your own game isn't a thing.
  const handleSurrenderAndSpectate = () => {
    window.location.href = `/games/${id}?spectate=true${
      fromCampaign ? '&from=campaign' : ''
    }`;
  };

  // "Next Mission" on the mission-complete panel. Creating the mission is the
  // same POST the campaign list and the landing page use, so unlock rules are
  // enforced in one place (missions.service.ts) — if the mission is locked or
  // the request fails for any reason, fall back to the campaign list, which is
  // where the player would have had to go anyway.
  //
  // Deliberately a full page load rather than navigate(): this component's
  // game-creation effect runs once on mount, so a same-route navigation from
  // /games/12 to /games/13 would keep the old Phaser instance and the old
  // game's socket. Same reasoning as handleSurrenderAndSpectate above.
  const handleNextMission = async (missionId: string) => {
    try {
      const result = await post(`missions/${missionId}/start`, {});
      window.location.href = `/games/${result.id}?from=campaign`;
    } catch {
      navigate('/campaign');
    }
  };

  // useEffect(() => {
  //     window.onscroll = function () {
  //         window.scrollTo(0, 0);
  //     }
  //     return () => { window.onscroll = function () { }; };
  // }, []);

  useEffect(() => {
    if (!container.current) {
      return;
    }
    const { width = 0, height = 0 } = container.current.getBoundingClientRect();
    while (container.current.firstChild) {
      container.current.removeChild(container.current.firstChild);
    }
    const game = createGame({
      config: { width, height, parent: 'game-container' },
      data: {
        url: uri,
        gameId: id,
        userId: auth?.sub,
        getToken: getFreshToken,
        spectate: isSpectator,
        onExit: handleExit,
        onNextMission: handleNextMission,
        onOpenSettings: handleOpenSettings,
        // No route back to spectating your own game in a campaign mission,
        // so the menu's "Surrender and spectate" option is dropped there
        // (the panel only renders the row when the callback is present).
        onSurrenderAndSpectate: fromCampaign
          ? undefined
          : handleSurrenderAndSpectate,
      },
    });

    return () => game.destroy(true);
  }, []);

  return (
    <div>
      {/* <div className={`modal ${openChat ? 'is-active' : ''}`}>
        <div className="modal-background"></div>
        <div className="modal-content">
          <Conversation conversationId="484b128c-aa22-4303-ba7e-2566d63e118e" />
        </div>
        <button
          className="modal-close is-large"
          aria-label="close"
          onClick={handleCloseChat}
        />
      </div>
      <div style={{ height: '40px' }}>
        <Link to={backUrl} className="button">
          Back
        </Link>
        {isSpectator && (
          <span style={{ marginLeft: 12, color: '#888', fontSize: 14 }}>
            Spectator Mode
          </span>
        )}
      </div> */}
      <div id="game-container" ref={container} className="fullscreen-canvas" />
    </div>
  );
}
